<!--
  Results shell — two-way switch:
    1. `showInitialSkeleton` → full-page skeleton (first load
       or date reset).
    2. Otherwise → real content; per-race switches use the
       inner `showResultsSkeleton` for the tables only.
-->
<!-- Profile panel takes over the page when opened from the header -->
@if (isProfileOpen) {
  <section class="profile-inline-panel tw-px-[28px] tw-pt-[16px]" data-cy="results-profile">
    <!-- Favorites stay visible above the profile takeover -->
    <ng-container *ngTemplateOutlet="favoritesBar"></ng-container>
    <feature-mat-profile/>
  </section>
}
@else if (showInitialSkeleton) {
  <ng-container *ngTemplateOutlet="refResultsInitialSkeleton"></ng-container>
}
@else {
  <ng-container *ngTemplateOutlet="refResultsContent"></ng-container>
}

<ng-template #refResultsContent>
<section
  class="results-content tw-px-[28px] tw-pt-[16px]"
  data-cy="results-screen"
>

  <!--
    Favorites bar — direct port of the wager page's favorites
    section. Rendered above the two-column layout so it spans the
    full page width, and only shown to signed-in users (the wager
    page uses the same `userEmail !== 'Guest'` gate).

    Chips are derived from the intersection of the account
    preferences favorites and the current finished-events list, so
    clicking one always resolves to a selectable event on this
    screen. The prev / next chevrons scroll the row horizontally
    when it overflows.
  -->
  <!--
    Favorites row — signed-in only. Shows skeleton chips while
    preferences load, real chips once matched, hides otherwise.
  -->
  <ng-container *ngTemplateOutlet="favoritesBar"></ng-container>

  <div class="tw-flex tw-flex-wrap">

    <!-- Left column: filters + race results -->
    <div class="tw-w-full lg:tw-w-[60%] lg:tw-pr-[20px]">

      <!-- tw-pt-[25px] -->
      <!-- tw-pr-[15px] -->
      <!-- tw-pl-[15px] -->
      <div class="tw-bg-[#E3ECF8] tw-rounded-[13px] tw-pb-[11px]">
        <!-- Top card -->
        <!-- tw-bg-[#EDF1F5] -->
        <!-- tw-pt-[25px] -->
        <div
          class="
            events-races-pools-modifiers-section
            tw-relative
            tw-rounded-[13px]
            tw-mb-[20px]
            tw-pt-[25px]
            tw-pr-[15px]
            tw-pb-[11px]
            tw-pl-[15px]
          "
        >
          <!-- Race info banner -->
          <div class="race-section" *ngIf="!invalidDate">
            <p class="races-info tw-text-[18px] tw-mt-[6px]" data-cy="races-info">
              <ng-container *ngIf="!isFetchingEventsData">
                <!-- Race status -->
                <span class="highlight tw-text-[#1E3F7F]" data-cy="race-status">
                  {{ selectedRace ? 'F' : (selectedEvent?.ToteResponse?.EventDetail?.EventStatus | getEventStatus) }}
                </span>

                <!-- Race number -->
                <span class="highlight" data-cy="race-number">
                  {{ selectedRace?.RaceId ? 'Race: ' + selectedRace?.RaceId : '' }}
                </span>

                <!-- Event track type -->
                <span data-cy="track-type">
                  {{ selectedEvent?.ToteResponse?.EventDetail?.TrackType | getTrackType }}
                </span>

                <!-- Race type -->
                <span data-cy="race-type">
                  {{ selectedRace?.RaceType }}
                </span>

                <!-- Race surface -->
                <span data-cy="race-surface">
                  {{ selectedRace?.Surface }}
                </span>

                <!-- Race distance -->
                <span data-cy="race-distance">
                  {{ selectedRace?.Distance }}
                </span>

                <!-- Race purse -->
                <span class="highlight tw-text-[#1E3F7F]" data-cy="race-purse">
                  {{
                    selectedRace?.Purse
                      ? 'Purse: $' + (selectedRace?.Purse | numberWithCommas: 0)
                      : ''
                  }}
                </span>
              </ng-container>
            </p>
          </div>

          <!-- Event / date / today row -->
          <div class="event-races-section tw-mt-[5px]">
            <mat-select
              #eventSelection
              name="event"
              placeholder="{{ 'ui.wager.event.list' | translate: 'Event List' }}"
              panelClass="select-event-track-results"
              class="
                horse-races
                tw-float-left
              "
              [value]="selectedEvent"
              [compareWith]="compareWith"
              [disabled]="isFetchingEventsData || errMsg !== ''"
              (selectionChange)="selectEvent($event.value)"
              [disableOptionCentering]="true"
              data-cy="results-event-dropdown"
            >
              <mat-option
                class="event-item"
                *ngFor="
                  let event of (events | sortEvents: sortBy: eventTypeFilter: setListFavorites);
                  trackBy: trackByFn
                "
                [value]="event"
              >
                <span data-cy="event-name">
                  {{
                    event?.CountryCode
                      ? event?.CountryCode === 'US' || event?.CountryCode === 'CA'
                        ? event?.ToteResponse?.EventDetail?.EventName
                        : event?.CountryCode + '-' + event?.ToteResponse?.EventDetail?.EventName
                      : event?.ToteResponse?.EventDetail?.EventName
                  }}
                </span>
              </mat-option>
            </mat-select>

            <!-- Date input -->
            <mat-form-field
              appearance="outline"
              class="date-input tw-float-left tw-w-[190px] tw-mr-[7px]"
              subscriptSizing="dynamic"
            >
              <input
                matInput
                [matDatepicker]="picker"
                [max]="maxDate"
                [value]="formVal['dateBy'].value"
                (dateChange)="setDate($event)"
                placeholder="{{ 'ui.wager.mm.dd.yyyy' | translate: 'MM/DD/YYYY' }}"
                data-cy="results-date-input"
              />
              <mat-datepicker-toggle
                matIconSuffix
                [for]="picker"
              >
                <img
                  matDatepickerToggleIcon
                  src="/assets/images/wager/calendar_icon.svg"
                  alt="{{ 'ui.wager.open.calendar' | translate: 'Open calendar' }}"
                  class="date-input__toggle-icon"
                />
              </mat-datepicker-toggle>
              <mat-datepicker #picker></mat-datepicker>
            </mat-form-field>

            <!-- Today shortcut -->
            <button
              mat-stroked-button
              type="button"
              class="today-btn tw-float-left"
              (click)="todayDate()"
              data-cy="results-today-btn"
            >
              {{ 'Today' | translate: 'Today' }}
            </button>
          </div>

          <!-- Race pagination -->
          <div
            *ngIf="!invalidDate && selectedEvent"
            class="
              race-selection-section
              races-paginate
              tw-relative
            "
          >
            <div class="
              tw-flex
              tw-items-center
              tw-mt-[5px]
              tw-pr-[15px]
              tw-pl-[6px]
            ">
              <div
                #swipeRaceSectionTemplateRef
                class="current-races tw-w-[465px]"
                data-cy="results-race-numbers"
              >
                <span
                  *ngFor="let race of selectedEvent.ToteResponse.EventDetail.Races"
                  (click)="selectRace(race)"
                  class="tw-cursor-pointer"
                  [class.prev-paginate]="race?.RaceStatus === 3"
                  [class.active-paginate]="selectedRace?.RaceId === race?.RaceId"
                  [attr.data-cy]="'results-race-number-' + race?.RaceId"
                >
                  {{ race?.RaceId }}
                </span>
              </div>
            </div>
          </div>

          <!-- Replay launcher -->
          <div
            [class.tw-opacity-30]="!isReplayAllowed()"
            [class.tw-pointer-events-none]="!isReplayAllowed()"
            class="
              tw-w-[43px]
              tw-absolute
              tw-top-[5px]
              tw-right-[18px]
            "
          >
            <div
              (click)="openReplayVideo()"
              class="
                play-video
                play-video-img
                tw-cursor-pointer
                tw-block
                tw-bg-[#133964]
                tw-border-[#A5A5A5]
                tw-border-[1px]
                tw-text-[#FFFFFF]
                tw-text-[38px]
                tw-py-[5px]
                tw-px-[7px]
                tw-rounded-[4px]
              "
              data-cy="results-replay-button"
            >
              <img
                class="play-icon"
                src="assets/images/wager/play_event_icon.svg"
                alt="{{ 'ui.wager.replay.icon' | translate: 'replay icon' }}"
                data-cy="results-replay-icon"
              >
            </div>

            <div class="tw-text-center tw-mt-[4px]">
              <p class="tw-text-[#FF6F6E] tw-text-[12px]">
                {{ 'ui.wager.replay' | translate: 'Replay' }}
              </p>
            </div>
          </div>

          <!-- Invalid date banner -->
          <p *ngIf="invalidDate" class="tw-text-red-500 tw-my-4">
            {{ 'ui.wager.the.date.is.not.valid' | translate: 'The date is not valid.' }}
          </p>
        </div>

          <!-- Result tables -->
          <section
            *ngIf="!invalidDate"
            class="race-final-results-section tw-pr-[15px] tw-pl-[15px]"
            data-cy="results-tables"
          >
            <!--
              Per-race loading — 2x2 skeleton mirroring the real
              tables. First paint only; refreshes keep old content.
            -->
            @if (showResultsSkeleton) {
              <div
                class="result-panels-grid"
                aria-hidden="true"
                data-cy="results-tables-skeleton"
              >
                <!-- Row 1 — Left: Runners skeleton -->
                <div class="result-panels-grid__cell result-panels-grid__cell--runners">
                  <div class="results-skeleton__table tw-p-3">
                    <div class="results-skeleton__bar tw-h-[18px] tw-w-[30%] tw-rounded-[4px] tw-mb-[12px]"></div>
                    @for (row of [1,2,3,4,5]; track row) {
                      <div class="tw-flex tw-items-center tw-gap-[10px] tw-mb-[10px]">
                        <div class="results-skeleton__bar tw-h-[16px] tw-w-[24px] tw-rounded-[4px]"></div>
                        <div class="results-skeleton__bar tw-h-[16px] tw-flex-1 tw-rounded-[4px]"></div>
                        <div class="results-skeleton__bar tw-h-[16px] tw-w-[50px] tw-rounded-[4px]"></div>
                        <div class="results-skeleton__bar tw-h-[16px] tw-w-[50px] tw-rounded-[4px]"></div>
                        <div class="results-skeleton__bar tw-h-[16px] tw-w-[50px] tw-rounded-[4px]"></div>
                      </div>
                    }
                  </div>
                </div>

                <!-- Row 1 — Right: Personal Statistics skeleton -->
                <div class="result-panels-grid__cell result-panels-grid__cell--stats">
                  <div class="results-skeleton__table tw-p-3">
                    <div class="results-skeleton__bar tw-h-[18px] tw-w-[60%] tw-rounded-[4px] tw-mb-[12px]"></div>
                    @for (row of [1,2,3,4]; track row) {
                      <div class="tw-flex tw-items-center tw-justify-between tw-mb-[10px]">
                        <div class="results-skeleton__bar tw-h-[14px] tw-w-[35%] tw-rounded-[4px]"></div>
                        <div class="results-skeleton__bar tw-h-[14px] tw-w-[40%] tw-rounded-[4px]"></div>
                      </div>
                    }
                  </div>
                </div>

                <!-- Row 2 — Left: Pool payouts skeleton -->
                <div class="result-panels-grid__cell result-panels-grid__cell--pools">
                  <div class="results-skeleton__table tw-p-3">
                    <div class="results-skeleton__bar tw-h-[18px] tw-w-[35%] tw-rounded-[4px] tw-mb-[12px]"></div>
                    @for (row of [1,2,3,4,5]; track row) {
                      <div class="tw-flex tw-items-center tw-gap-[10px] tw-mb-[10px]">
                        <div class="results-skeleton__bar tw-h-[16px] tw-w-[22%] tw-rounded-[4px]"></div>
                        <div class="results-skeleton__bar tw-h-[16px] tw-w-[18%] tw-rounded-[4px]"></div>
                        <div class="results-skeleton__bar tw-h-[16px] tw-w-[18%] tw-rounded-[4px]"></div>
                        <div class="results-skeleton__bar tw-h-[16px] tw-w-[18%] tw-rounded-[4px]"></div>
                        <div class="results-skeleton__bar tw-h-[16px] tw-w-[24%] tw-rounded-[4px]"></div>
                      </div>
                    }
                  </div>
                </div>

                <!-- Row 2 — Right: Scratches skeleton -->
                <div class="result-panels-grid__cell result-panels-grid__cell--scratches">
                  <div class="results-skeleton__table tw-p-3">
                    <div class="results-skeleton__bar tw-h-[18px] tw-w-[55%] tw-rounded-[4px] tw-mb-[12px]"></div>
                    @for (row of [1,2,3]; track row) {
                      <div class="tw-flex tw-items-center tw-gap-[12px] tw-mb-[10px]">
                        <div class="results-skeleton__bar tw-h-[16px] tw-w-[28px] tw-rounded-[4px]"></div>
                        <div class="results-skeleton__bar tw-h-[20px] tw-flex-1 tw-rounded-[3px]"></div>
                      </div>
                    }
                  </div>
                </div>
              </div>
            }

            <!-- No results yet — friendly empty-state card. -->
            <div
              *ngIf="!showResultsSkeleton && !isFetchingData && !raceResults && !errMsg"
              class="results-empty"
              role="status"
              aria-live="polite"
              data-cy="results-empty-state"
            >
              <div class="results-empty__card">
                <div class="results-empty__illustration" aria-hidden="true">
                  <svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id="resultsEmptyGrad" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0" stop-color="#557DAC"/>
                        <stop offset="1" stop-color="#1E3F7F"/>
                      </linearGradient>
                    </defs>
                    <circle cx="60" cy="60" r="56" fill="url(#resultsEmptyGrad)" opacity="0.12"/>
                    <circle cx="60" cy="60" r="42" fill="url(#resultsEmptyGrad)" opacity="0.2"/>
                    <!-- Trophy silhouette -->
                    <path
                      d="M45 40h30v4h5a7 7 0 0 1 7 7v4a11 11 0 0 1-11 11h-1a15 15 0 0 1-11 11v8h8v4H48v-4h8v-8a15 15 0 0 1-11-11h-1a11 11 0 0 1-11-11v-4a7 7 0 0 1 7-7h5v-4zm-5 8a3 3 0 0 0-3 3v4a7 7 0 0 0 7 7h1v-14h-5zm40 0v14h1a7 7 0 0 0 7-7v-4a3 3 0 0 0-3-3h-5z"
                      fill="#1E3F7F"
                    />
                  </svg>
                </div>

                <h3 class="results-empty__title" data-cy="results-empty-title">
                  {{ 'ui.wager.results.not.in.yet' | translate: 'Results not in yet' }}
                </h3>

                <p class="results-empty__message" data-cy="results-empty-message">
                  {{ 'ui.wager.official.payouts.for.this.race.havent' | translate: 'Official payouts for this race haven\'t posted yet. They\'ll appear here shortly.' }}
                </p>
              </div>
            </div>

            <!-- Error state — same card layout, warning palette. -->
            <div
              *ngIf="errMsg"
              class="results-empty results-empty--error"
              role="alert"
              data-cy="results-error-state"
            >
              <div class="results-empty__card">
                <div class="results-empty__illustration" aria-hidden="true">
                  <svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id="resultsErrGrad" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0" stop-color="#F5A67C"/>
                        <stop offset="1" stop-color="#D64545"/>
                      </linearGradient>
                    </defs>
                    <circle cx="60" cy="60" r="56" fill="url(#resultsErrGrad)" opacity="0.12"/>
                    <circle cx="60" cy="60" r="42" fill="url(#resultsErrGrad)" opacity="0.2"/>
                    <!-- Warning triangle -->
                    <path
                      d="M60 34l30 52H30l30-52zm0 12L40 82h40L60 46zm-3 12v14h6V58h-6zm0 18v6h6v-6h-6z"
                      fill="#D64545"
                    />
                  </svg>
                </div>

                <h3 class="results-empty__title results-empty__title--error" data-cy="results-error-title">
                  {{ 'ui.wager.we.couldnt.load.these.results' | translate: 'We couldn\'t load these results' }}
                </h3>

                <p class="results-empty__message" data-cy="results-error-message">
                  {{ errMsg }}
                </p>
              </div>
            </div>

            <!-- Result panels grid -->
            <div
              *ngIf="raceResults && !isFetchingData"
              class="result-panels-grid"
              data-cy="result-panels-grid"
            >
              <!-- Row 1 — Left: Runners -->
              <div class="result-panels-grid__cell result-panels-grid__cell--runners">
                <table
                  class="table-result-main tw-w-full tw-text-[12px]"
                  data-cy="tbl-top-runners"
                >
                  <thead class="tw-bg-[#C7C7C6]">
                    <tr>
                      <th class="tw-text-left tw-py-[6px] tw-px-[8px]">{{ 'result.position' | translate: 'Pos.' }}</th>
                      <th class="tw-text-center tw-py-[6px] tw-px-[8px]">{{ 'generic.runner' | translate: 'Runner' }}</th>
                      <th class="tw-text-center tw-py-[6px] tw-px-[8px]"></th>
                      <th class="tw-text-center tw-py-[6px] tw-px-[8px]">{{ 'WIN' | translate: 'Win' }}</th>
                      <th class="tw-text-center tw-py-[6px] tw-px-[8px]">{{ 'generic.place' | translate: 'Place' }}</th>
                      <th class="tw-text-center tw-py-[6px] tw-px-[8px]">{{ 'generic.show' | translate: 'Show' }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <ng-container *ngFor="let item of raceResults.RaceResults.Positions">
                      <tr *ngFor="let runner of item?.Finishers; let i = index">
                        <td class="tw-text-center tw-py-[.3rem]">
                          {{ item?.PositionId }}
                        </td>
                        <td class="tw-py-[.3rem] tw-px-[8px]">
                          <div class="tw-font-bold">{{ runner?.Name }}</div>
                          <div>{{ 'wager.jockey' | translate: 'Jockey' }}: {{ runner?.Jockey }}</div>
                          <div>
                            {{ 'wager.trainer' | translate: 'Trainer' }}:
                            {{
                              selectedRace?.Runners?.[(runner?.RunnerId || 0) - 1]?.Entries?.[0]?.Trainer
                            }}
                          </div>
                          <div>
                            {{ 'ui.wager.owner' | translate: 'Owner' }}:
                            {{
                              selectedRace?.Runners?.[(runner?.RunnerId || 0) - 1]?.Entries?.[0]?.Owner
                            }}
                          </div>
                        </td>
                        <td
                          class="{{ (selectedEvent?.ToteResponse?.EventDetail?.TrackType | getTrackType: true) + '-' + runner?.RunnerId }} tw-text-center"
                        >
                          {{ runner?.RunnerId }}
                        </td>
                        <td class="tw-text-center">
                          {{ raceResults | raceResult: 'MAIN': runner || 0: 'WIN' | numberWithCommas }}
                        </td>
                        <td class="tw-text-center">
                          {{ raceResults | raceResult: 'MAIN': runner || 0: 'PLC' | numberWithCommas }}
                        </td>
                        <td class="tw-text-center">
                          {{ raceResults | raceResult: 'MAIN': runner || 0: 'SHW' | numberWithCommas }}
                        </td>
                      </tr>
                    </ng-container>
                  </tbody>
                </table>
              </div>

              <!-- Row 1 — Right: Personal Statistics -->
              <div class="result-panels-grid__cell result-panels-grid__cell--stats">
                <table
                  class="table-personal-statistics tw-w-full tw-text-[12px]"
                  data-cy="tbl-personal-stats"
                >
                  <thead class="tw-bg-[#C7C7C6]">
                    <tr>
                      <!-- Icon column — aligned with Wagers/Wagered value cells below -->
                      <th class="tw-text-left tw-py-[6px] tw-pl-2 tw-w-[24px]">
                        <img
                          src="/assets/images/wager/personal_statistics_icon.svg"
                          class="table-panel-heading__icon-img"
                          alt="{{ 'result.personalstats' | translate: 'Personal Statistics' }}"
                        >
                      </th>
                      <!-- Label column -->
                      <th class="tw-text-left tw-py-[6px]">
                        <span class="table-panel-heading__label-bold">{{ 'result.personalstats' | translate: 'Personal Statistics' }}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="tw-pl-2 tw-py-[.3rem]">{{ 'generic.wagers' | translate: 'Wagers' }}</td>
                      <td class="tw-text-right tw-pr-2">
                        {{ personalStatistic?.ticketCounter }}
                      </td>
                    </tr>
                    <tr>
                      <td class="tw-pl-2 tw-py-[.3rem]">{{ 'generic.wagered' | translate: 'Wagered' }}</td>
                      <td class="tw-text-right tw-pr-2">
                        ${{ personalStatistic?.ticketAmount }}
                      </td>
                    </tr>
                    <tr>
                      <td class="tw-pl-2 tw-py-[.3rem]">{{ 'generic.return' | translate: 'Return' }}</td>
                      <td class="tw-text-right tw-pr-2">
                        ${{ personalStatistic?.earnings }}
                      </td>
                    </tr>
                    <tr>
                      <td class="tw-pl-2 tw-py-[.3rem]">P/L</td>
                      <td class="tw-text-right tw-pr-2">
                        ${{ personalStatistic?.profitLoss }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Row 2 — Left: Pool payouts -->
              <div class="result-panels-grid__cell result-panels-grid__cell--pools">
                <table
                  class="table-result-pool tw-w-full tw-text-[12px]"
                  data-cy="tbl-results"
                >
                  <thead class="tw-bg-[#C7C7C6]">
                    <tr>
                      <th class="tw-text-left tw-py-[6px] tw-px-[8px] tw-w-[20%]">{{ 'generic.poolname' | translate: 'Pool Name' }}</th>
                      <th class="tw-text-center tw-py-[6px] tw-w-[20%]">{{ 'result.finish' | translate: 'Finish' }}</th>
                      <th class="tw-text-center tw-py-[6px] tw-w-[20%]">{{ 'main.wager' | translate: 'Wager' }}</th>
                      <th class="tw-text-center tw-py-[6px] tw-w-[20%]">{{ 'generic.payout' | translate: 'Payout' }}</th>
                      <th class="tw-text-right tw-py-[6px] tw-pr-[8px] tw-w-[30%]">{{ 'result.totalpool' | translate: 'Total Pool' }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <ng-container *ngFor="let poolPrice of poolPrices">
                      <ng-container *ngIf="poolPrice?.Refund">
                        <tr>
                          <td class="tw-py-[.3rem] tw-text-left tw-pl-2">{{ poolPrice?.PoolName }}</td>
                          <td class="tw-text-center">{{ 'generic.refund' | translate: 'Refund' }}</td>
                          <td class="tw-text-center">{{ 'generic.refund' | translate: 'Refund' }}</td>
                          <td class="tw-text-center">{{ 'generic.refund' | translate: 'Refund' }}</td>
                          <td class="tw-text-right tw-pr-2">{{ 'generic.refund' | translate: 'Refund' }}</td>
                        </tr>
                      </ng-container>

                      <ng-container *ngIf="!poolPrice?.Refund">
                        <tr *ngFor="let price of poolPrice?.Prices">
                          <td class="tw-py-[.3rem] tw-text-left tw-pl-2">{{ poolPrice?.PoolName }}</td>
                          <td class="tw-text-center">
                            {{ price?.Finish | numberWithCommas }}
                          </td>
                          <td class="tw-text-center">
                            {{ price?.Wager ? '$ ' + (price?.Wager | numberWithCommas) : price?.Wager }}
                          </td>
                          <td class="tw-text-center">
                            {{
                              price?.PriceAmount
                                ? '$ ' + (price?.PriceAmount | numberWithCommas)
                                : price?.PriceAmount
                            }}
                          </td>
                          <td class="tw-text-right tw-pr-2">
                            {{ poolsTotal | raceResult: 'POOL': poolPrice || 0 | numberWithCommas }}
                          </td>
                        </tr>
                      </ng-container>
                    </ng-container>

                    <tr class="tw-font-bold">
                      <td class="tw-py-[.3rem] tw-text-left tw-pl-2" colspan="2">{{ 'result.racehandle' | translate: 'Race Handle' }}</td>
                      <td class="tw-text-right tw-pr-2" colspan="3">
                        {{ '$ ' + (totalHandle?.toFixed() | numberWithCommas) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Row 2 — Right: Scratches -->
              <div class="result-panels-grid__cell result-panels-grid__cell--scratches">
                <table
                  class="table-result-scratches tw-w-full tw-text-[12px]"
                  data-cy="tbl-scratches"
                >
                  <thead class="tw-bg-[#C7C7C6]">
                    <tr>
                      <!-- Scratches column: warning icon + label -->
                      <th class="tw-text-left tw-py-[6px] tw-px-[8px] tw-w-[40px]">
                        <span class="table-panel-heading table-panel-heading--warning">
                          <img
                            src="/assets/images/wager/scratches_warning_icon.svg"
                            class="table-panel-heading__icon-img"
                            alt="{{ 'generic.scratches' | translate: 'Scratches' }}"
                          >
                          <span class="table-panel-heading__label">{{ 'generic.scratches' | translate: 'Scratches' }}</span>
                        </span>
                      </th>
                      <!-- Runner column: centred within the runner-name cell -->
                      <th class="tw-text-center tw-py-[6px] tw-px-[8px]">
                        <span class="table-panel-heading__hint">{{ 'generic.runner' | translate: 'Runner' }}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <ng-container *ngIf="raceResults.RaceResults.Scratches as scratches">
                      <tr *ngFor="let item of (scratches | stringToArrary)">
                        <td class="tw-py-[.3rem] tw-pl-2 tw-w-[40px]">
                          {{ item }}
                        </td>
                        <!-- tw-py-[.3rem] tw-pr-2 -->
                        <td class="">
                          <span
                            class="scratch-runner-badge tw-w-full tw-capitalize {{ (selectedEvent?.ToteResponse?.EventDetail?.TrackType | getTrackType: true) + '-' + item }}"
                          >
                            {{ (selectedRace?.Runners | getRunner: item)?.Name || '—' }}
                          </span>
                        </td>
                      </tr>
                    </ng-container>

                    <ng-container *ngIf="!raceResults.RaceResults.Scratches">
                      <tr>
                        <td
                          colspan="2"
                          class="tw-text-[12px] tw-py-[.6rem] tw-px-2 tw-text-center"
                        >
                          {{ 'result.noscratches' | translate: 'No Scratches' }}
                        </td>
                      </tr>
                    </ng-container>
                  </tbody>
                </table>
              </div>
            </div>
          </section>
      </div>

    </div>

    <!-- Right column: My Bets + Pool Data -->
    <div class="tw-w-full lg:tw-w-[40%]">

      <!-- My Bets -->
      <feature-mat-bets/>

      <!-- Pool Data (matrix) -->
      <feature-mat-matrix
        [selectedRaceInput]="selectedRace"
        [selectedEventInput]="selectedEvent"
        [isOnline]="true"
        [currentRaceStatus]="3"
        [isReplayVideo]="true"
        (replayVideoRequested)="openReplayVideo()"
      />
    </div>
  </div>
</section>
</ng-template>

<!--
  Full-page skeleton — first fetch / date reset. Mirrors the
  real 60/40 layout; SR announcement handled by HorseLoaderService.
-->
<ng-template #refResultsInitialSkeleton>
  <section
    class="results-content results-skeleton tw-px-[28px] tw-pt-[16px]"
    aria-hidden="true"
    data-cy="results-loading-skeleton"
  >
    <!-- Favorites row skeleton — signed-in users only. -->
    @if (userEmail !== undefined && userEmail !== 'Guest') {
      <div
        class="favorites-section tw-mb-[12px]"
        data-cy="results-favorites-row-skeleton"
      >
        <div class="results-skeleton__bar tw-w-[24px] tw-h-[24px] tw-rounded-[4px] tw-mr-[19px] tw-float-left"></div>
        <div class="results-skeleton__bar tw-w-[80px] tw-h-[22px] tw-rounded-[4px] tw-relative tw-top-[3px] tw-mt-[5px] tw-float-left"></div>
        <div class="results-skeleton__bar tw-w-[25px] tw-h-[25px] tw-rounded-[4px] tw-relative tw-top-[3px] tw-mt-[5px] tw-ml-[20px] tw-mr-[20px] tw-float-left"></div>
        <div class="favorite-lists tw-w-[80%] tw-flex tw-mt-[5px] tw-float-left">
          @for (chip of [1,2,3,4]; track chip) {
            <div class="results-skeleton__bar tw-w-[273px] tw-h-[30px] tw-rounded-[4px] tw-mr-[18px] tw-flex-shrink-0"></div>
          }
        </div>
        <div class="results-skeleton__bar tw-w-[25px] tw-h-[25px] tw-rounded-[4px] tw-relative tw-top-[3px] tw-mt-[5px] tw-ml-[20px] tw-mr-[20px] tw-float-left"></div>
      </div>
    }

    <div class="tw-flex tw-flex-wrap">
      <!-- Left column skeleton — filter row + race chips + 2x2 tables grid -->
      <div class="tw-w-full lg:tw-w-[60%] lg:tw-pr-[20px]">
        <!-- Top card: mirrors `events-races-pools-modifiers-section` -->
        <div
          class="
            results-skeleton__panel
            tw-relative
            tw-rounded-[13px]
            tw-bg-[#EDF1F5]
            tw-mb-[20px]
            tw-pt-[25px]
            tw-pr-[15px]
            tw-pb-[11px]
            tw-pl-[15px]
          "
        >
          <!-- Race info banner placeholder -->
          <div class="results-skeleton__bar tw-h-[20px] tw-w-[65%] tw-rounded-[4px] tw-mb-[16px]"></div>

          <!-- Event / date / today row placeholders -->
          <div class="tw-flex tw-gap-[10px] tw-mb-[18px]">
            <div class="results-skeleton__bar tw-h-[40px] tw-w-[292px] tw-rounded-[20px]"></div>
            <div class="results-skeleton__bar tw-h-[40px] tw-w-[190px] tw-rounded-[7px]"></div>
            <div class="results-skeleton__bar tw-h-[40px] tw-w-[80px] tw-rounded-[7px]"></div>
          </div>

          <!-- Race chips placeholder -->
          <div class="tw-flex tw-gap-[7px] tw-flex-wrap tw-mt-[5px]">
            @for (chip of [1,2,3,4,5,6,7,8]; track chip) {
              <div class="results-skeleton__bar tw-h-[29px] tw-w-[29px] tw-rounded-[4px]"></div>
            }
          </div>

          <!-- Replay tile placeholder — pinned to the top-right corner -->
          <div class="tw-w-[43px] tw-absolute tw-top-[5px] tw-right-[18px]">
            <div class="results-skeleton__bar tw-h-[48px] tw-w-full tw-rounded-[4px]"></div>
            <div class="results-skeleton__bar tw-h-[10px] tw-w-[70%] tw-mt-[6px] tw-mx-auto tw-rounded-[4px]"></div>
          </div>
        </div>

        <!-- 2x2 results tables skeleton — matches `.result-panels-grid` -->
        <div class="result-panels-grid" data-cy="results-skeleton-grid">
          <div class="result-panels-grid__cell result-panels-grid__cell--runners">
            <div class="results-skeleton__table tw-p-3">
              <div class="results-skeleton__bar tw-h-[18px] tw-w-[30%] tw-rounded-[4px] tw-mb-[12px]"></div>
              @for (row of [1,2,3,4,5]; track row) {
                <div class="tw-flex tw-items-center tw-gap-[10px] tw-mb-[10px]">
                  <div class="results-skeleton__bar tw-h-[16px] tw-w-[24px] tw-rounded-[4px]"></div>
                  <div class="results-skeleton__bar tw-h-[16px] tw-flex-1 tw-rounded-[4px]"></div>
                  <div class="results-skeleton__bar tw-h-[16px] tw-w-[50px] tw-rounded-[4px]"></div>
                  <div class="results-skeleton__bar tw-h-[16px] tw-w-[50px] tw-rounded-[4px]"></div>
                  <div class="results-skeleton__bar tw-h-[16px] tw-w-[50px] tw-rounded-[4px]"></div>
                </div>
              }
            </div>
          </div>

          <div class="result-panels-grid__cell result-panels-grid__cell--stats">
            <div class="results-skeleton__table tw-p-3">
              <div class="results-skeleton__bar tw-h-[18px] tw-w-[60%] tw-rounded-[4px] tw-mb-[12px]"></div>
              @for (row of [1,2,3,4]; track row) {
                <div class="tw-flex tw-items-center tw-justify-between tw-mb-[10px]">
                  <div class="results-skeleton__bar tw-h-[14px] tw-w-[35%] tw-rounded-[4px]"></div>
                  <div class="results-skeleton__bar tw-h-[14px] tw-w-[40%] tw-rounded-[4px]"></div>
                </div>
              }
            </div>
          </div>

          <div class="result-panels-grid__cell result-panels-grid__cell--pools">
            <div class="results-skeleton__table tw-p-3">
              <div class="results-skeleton__bar tw-h-[18px] tw-w-[35%] tw-rounded-[4px] tw-mb-[12px]"></div>
              @for (row of [1,2,3,4,5]; track row) {
                <div class="tw-flex tw-items-center tw-gap-[10px] tw-mb-[10px]">
                  <div class="results-skeleton__bar tw-h-[16px] tw-w-[22%] tw-rounded-[4px]"></div>
                  <div class="results-skeleton__bar tw-h-[16px] tw-w-[18%] tw-rounded-[4px]"></div>
                  <div class="results-skeleton__bar tw-h-[16px] tw-w-[18%] tw-rounded-[4px]"></div>
                  <div class="results-skeleton__bar tw-h-[16px] tw-w-[18%] tw-rounded-[4px]"></div>
                  <div class="results-skeleton__bar tw-h-[16px] tw-w-[24%] tw-rounded-[4px]"></div>
                </div>
              }
            </div>
          </div>

          <div class="result-panels-grid__cell result-panels-grid__cell--scratches">
            <div class="results-skeleton__table tw-p-3">
              <div class="results-skeleton__bar tw-h-[18px] tw-w-[55%] tw-rounded-[4px] tw-mb-[12px]"></div>
              @for (row of [1,2,3]; track row) {
                <div class="tw-flex tw-items-center tw-gap-[12px] tw-mb-[10px]">
                  <div class="results-skeleton__bar tw-h-[16px] tw-w-[28px] tw-rounded-[4px]"></div>
                  <div class="results-skeleton__bar tw-h-[20px] tw-flex-1 tw-rounded-[3px]"></div>
                </div>
              }
            </div>
          </div>
        </div>
      </div>

      <!-- Right column skeleton — My Bets + Pool Data placeholders -->
      <div class="tw-w-full lg:tw-w-[40%]">
        <div class="results-skeleton__panel tw-rounded-[13px] tw-bg-[#EDF1F5] tw-p-[18px] tw-mb-[16px]">
          <div class="results-skeleton__bar tw-h-[18px] tw-w-[35%] tw-rounded-[4px] tw-mb-[14px]"></div>
          <div class="results-skeleton__bar tw-h-[110px] tw-w-full tw-rounded-[8px]"></div>
        </div>

        <div class="results-skeleton__panel tw-rounded-[13px] tw-bg-[#EDF1F5] tw-p-[18px]">
          <div class="results-skeleton__bar tw-h-[18px] tw-w-[30%] tw-rounded-[4px] tw-mb-[14px]"></div>
          <div class="results-skeleton__bar tw-h-[160px] tw-w-full tw-rounded-[8px]"></div>
        </div>
      </div>
    </div>
  </section>
</ng-template>

<!-- Favorites row — shared so it shows in content and above the profile panel -->
<ng-template #favoritesBar>
  @if (
    userEmail !== undefined
    && userEmail !== 'Guest'
  ) {
    <div
      class="
        favorites-section
        tw-mb-[12px]
      "
      data-cy="results-favorites"
    >
      <img
        src="/assets/images/wager/favorites_icon.svg"
        class="tw-inline tw-mr-[19px] tw-float-left"
        alt="favorites"
      >
      <span
        class="
          tw-text-[18px]
          tw-relative
          tw-top-[3px]
          tw-mt-[5px]
          tw-float-left
        "
      >
        {{ 'ui.wager.favorites' | translate: 'Favorites' }}
      </span>
      <mat-icon
        (click)="scrollTo('prev')"
        class="
          tw-text-[25px]
          tw-text-[#B5C5D8]
          tw-relative
          tw-cursor-pointer
          tw-top-[3px]
          tw-mt-[5px]
          tw-ml-[20px]
          tw-mr-[20px]
          tw-float-left
        "
        data-cy="results-favorites-prev"
      >
        arrow_back
      </mat-icon>
      <div
        #favorites
        class="favorite-lists tw-w-[80%] tw-flex tw-mt-[5px] tw-float-left tw-overflow-hidden"
      >
        <!-- Skeleton chips while favorites preference resolves. -->
        @if (showFavoritesSkeleton) {
          <ul
            class="tw-text-center tw-flex"
            aria-hidden="true"
            data-cy="results-favorites-skeleton"
          >
            @for (chip of [1,2,3,4]; track chip) {
              <li
                class="
                  results-skeleton__bar
                  tw-w-[273px]
                  tw-h-[30px]
                  tw-inline-block
                  tw-rounded-[4px]
                  tw-mr-[18px]
                "
              ></li>
            }
          </ul>
        }
        @else {
          <ul class="tw-text-center tw-flex">
            <li
              *ngFor="let favorite of listOfFavoriteEvents; trackBy: trackByFn"
              (click)="selectFavorite(favorite)"
              class="
                tw-text-[#FFFFFF]
                tw-w-[273px]
                tw-h-[30px]
                tw-content-center
                tw-inline-block
                tw-rounded-[4px]
                tw-bg-[#557DAC]
                tw-mr-[18px]
              "
              [ngClass]="{'active-event' : selectedEvent?.id === favorite.id}"
              [attr.data-cy]="'results-favorite-' + favorite.id"
            >
              <!-- Mirror #eventSelection: track (country code), race, status/MTP -->
              <span data-cy="favorite-event-name">
                {{
                  favorite?.CountryCode
                    ? favorite?.CountryCode === 'US' || favorite?.CountryCode === 'CA'
                      ? favorite?.ToteResponse?.EventDetail?.EventName
                      : favorite?.CountryCode + '-' + favorite?.ToteResponse?.EventDetail?.EventName
                    : favorite?.ToteResponse?.EventDetail?.EventName
                }}
              </span>

              <span data-cy="favorite-current-race">
                {{
                  favorite?.ToteResponse?.EventDetail?.currentRace?.RaceId
                    ? '('+(favorite?.ToteResponse?.EventDetail?.TrackType | getTrackType).charAt(0)
                      + ' R'+favorite?.ToteResponse?.EventDetail?.currentRace?.RaceId+')'
                    : ''
                }}
              </span>

              <span data-cy="favorite-event-status">
                {{
                  favorite?.ToteResponse?.EventDetail?.currentRace
                    ? (
                        (favorite?.ToteResponse?.EventDetail?.currentRace)?.RaceStatus
                          | getRaceStatus: (
                            favorite?.ToteResponse?.EventDetail?.currentRace
                          )?.PostTime: favorite?.eventName
                          | countdown: false: favorite?.eventName | async
                      )
                    : (favorite?.ToteResponse?.EventDetail?.EventStatus | getEventStatus)
                }}
              </span>
            </li>
          </ul>
        }
      </div>
      <mat-icon
        (click)="scrollTo('next')"
        class="
          tw-text-[25px]
          tw-text-[#B5C5D8]
          tw-relative
          tw-cursor-pointer
          tw-top-[3px]
          tw-mt-[5px]
          tw-ml-[20px]
          tw-mr-[20px]
          tw-float-left
        "
        data-cy="results-favorites-next"
      >
        arrow_forward
      </mat-icon>
    </div>
  }
</ng-template>
